import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { compressImage } from "../lib/photoUpload";
import { validateImageFile } from "../lib/fileValidation";
import {
  PM4SUBS_API_URL,
  R2_PUBLIC_URL,
  PHOTO_UPLOAD_URL,
} from "../lib/apiConfig";
import * as api from "../lib/supabase";
import VoiceButton from "../components/voice/VoiceButton";
import { useApp } from "../lib/AppContext";
import { OfflineBanner } from "../components/OfflineBanner";
import { EventTypes } from "../lib/offlineQueue";

// Photo categories
const CATEGORIES = [
  { value: "progress", label: "Progress" },
  { value: "safety", label: "Safety" },
  { value: "quality", label: "Quality" },
  { value: "issue", label: "Issue/Problem" },
  { value: "completed", label: "Completed Work" },
  { value: "material", label: "Material/Delivery" },
  { value: "other", label: "Other" },
];

export default function Camera({ job, currentUser, orgId, location, onBack }) {
  const { offlineQueue } = useApp();
  const {
    isOnline,
    isSyncing,
    pendingCount,
    pendingByType,
    queueEvent,
    syncAll,
  } = offlineQueue;

  const [photos, setPhotos] = useState([]);
  const [photoUrls, setPhotoUrls] = useState({}); // Cache of display URLs
  const [loading, setLoading] = useState(false);
  const [pendingPhotos, setPendingPhotos] = useState([]); // Photos waiting to upload
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({
    current: 0,
    total: 0,
  });
  const [category, setCategory] = useState("progress");
  const [notes, setNotes] = useState("");
  const [isRecordingVoice, setIsRecordingVoice] = useState(false);
  const [rawTranscript, setRawTranscript] = useState(""); // Store original voice transcript
  const [showTranscript, setShowTranscript] = useState(false); // Toggle to show raw transcript
  const fileInputRef = useRef(null);

  // Pending photo count from offline queue
  const queuedPhotoCount = pendingByType[EventTypes.PHOTO_ADD] || 0;

  // Handle voice recording result - extract structured data for photo description
  const handleVoiceResult = (result) => {
    if (!result) return;

    // Store raw transcript for "show original" feature
    setRawTranscript(result.transcript || "");

    const extracted = result.extracted || {};
    let parts = [];

    // Build structured note from extracted data
    if (extracted.location) parts.push(`Location: ${extracted.location}`);
    if (extracted.status) parts.push(`Status: ${extracted.status}`);
    if (extracted.activity) parts.push(`Activity: ${extracted.activity}`);
    if (extracted.area) parts.push(`Area: ${extracted.area}`);
    if (extracted.quantities?.length > 0) {
      const qtyText = extracted.quantities
        .map((q) => `${q.qty} ${q.item}`)
        .join(", ");
      parts.push(`Quantities: ${qtyText}`);
    }
    if (extracted.notes) parts.push(`Notes: ${extracted.notes}`);
    if (extracted.issues?.length > 0) {
      parts.push(`[ISSUE] ${extracted.issues.join(", ")}`);
    }

    // If we got structured data, use it; otherwise fall back to transcript
    const noteText = parts.length > 0 ? parts.join(" | ") : result.transcript;
    setNotes(noteText || "");

    // Auto-set category to "issue" if issues were detected
    if (extracted.issues?.length > 0) {
      setCategory("issue");
    }
  };

  // Load today's photos for this job
  useEffect(() => {
    loadPhotos();
  }, [job.id]);

  // Get display URL for a photo
  const getDisplayUrl = async (photoUrl) => {
    if (!photoUrl) return null;

    // Check cache first
    if (photoUrls[photoUrl]) return photoUrls[photoUrl];

    // PM4Subs R2 paths contain /projects/ - need presigned URL
    if (photoUrl.includes("/projects/")) {
      try {
        const { data: sessionData } = await api.supabase.auth.getSession();
        const token = sessionData?.session?.access_token;
        if (!token) return null;

        const response = await fetch(`${PM4SUBS_API_URL}/api/r2-download-url`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ filePath: photoUrl }),
        });

        if (response.ok) {
          const { downloadUrl } = await response.json();
          setPhotoUrls((prev) => ({ ...prev, [photoUrl]: downloadUrl }));
          return downloadUrl;
        }
      } catch (err) {
        console.error("Failed to get presigned URL:", err);
      }
      return null;
    }

    // Old public R2 path
    const url = `${R2_PUBLIC_URL}/${photoUrl}`;
    setPhotoUrls((prev) => ({ ...prev, [photoUrl]: url }));
    return url;
  };

  const loadPhotos = async () => {
    setLoading(true);
    try {
      const { data, error } = await api.supabase
        .from("t4s_job_photos")
        .select("id, photo_url, taken_at, category, description")
        .eq("job_id", job.id)
        .gte("taken_at", new Date().toLocaleDateString("en-CA"))
        .order("taken_at", { ascending: false });

      if (error) throw error;

      let photosData = data || [];

      // Filter out photos that have been deleted in PM4Subs
      // Photos with /projects/ path are stored in PM4Subs - check if they're deleted
      const pm4subsPaths = photosData
        .filter((p) => p.photo_url?.includes("/projects/"))
        .map((p) => p.photo_url);

      if (pm4subsPaths.length > 0) {
        const { data: activePhotos } = await api.supabase
          .from("project_photos")
          .select("storage_path")
          .in("storage_path", pm4subsPaths)
          .is("deleted_at", null);

        const activePaths = new Set(
          (activePhotos || []).map((p) => p.storage_path),
        );

        // Keep photos that are either not in PM4Subs or are still active
        photosData = photosData.filter(
          (p) =>
            !p.photo_url?.includes("/projects/") ||
            activePaths.has(p.photo_url),
        );
      }

      setPhotos(photosData);

      // Pre-fetch display URLs for all photos
      for (const photo of photosData) {
        if (photo.photo_url) {
          getDisplayUrl(photo.photo_url);
        }
      }
    } catch (error) {
      console.error("Error loading photos:", error);
      setPhotos([]);
    } finally {
      setLoading(false);
    }
  };

  // Open file picker for multiple photos
  const selectPhotos = () => {
    fileInputRef.current?.click();
  };

  // Handle file selection - just add to queue, user can tap "Take More" or "Upload"
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Clear input so same files can be selected again
    e.target.value = "";

    // Add all selected files to pending (up to 10 total)
    const newPending = [...pendingPhotos];
    let validationErrors = [];

    for (const file of files) {
      if (newPending.length >= 10) {
        toast.warning("Maximum 10 photos reached.");
        break;
      }

      // Validate file type and size
      const validation = await validateImageFile(file, {
        maxSize: 20 * 1024 * 1024,
      });
      if (!validation.valid) {
        validationErrors.push(`${file.name}: ${validation.error}`);
        continue;
      }

      const previewUrl = URL.createObjectURL(file);
      newPending.push({ file, previewUrl });
    }

    if (validationErrors.length > 0) {
      toast.error(validationErrors.join("\n"));
    }

    setPendingPhotos(newPending);
  };

  // Remove a photo from pending list
  const removePendingPhoto = (index) => {
    setPendingPhotos((prev) => {
      const newList = [...prev];
      URL.revokeObjectURL(newList[index].previewUrl);
      newList.splice(index, 1);
      return newList;
    });
  };

  // Get GPS location
  const getLocation = () => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) =>
          resolve({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
          }),
        () => resolve(null),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 },
      );
    });
  };

  // Upload all pending photos (or queue if offline)
  const uploadAll = async () => {
    if (pendingPhotos.length === 0 || uploading) return;

    setUploading(true);
    setUploadProgress({ current: 0, total: pendingPhotos.length });

    // Get GPS once for all photos
    const gps = await getLocation();

    // If offline, queue photos for later sync
    if (!isOnline) {
      let queuedCount = 0;

      for (let i = 0; i < pendingPhotos.length; i++) {
        setUploadProgress({ current: i + 1, total: pendingPhotos.length });

        try {
          const { file } = pendingPhotos[i];

          // Compress before storing
          const compressed = await compressImage(file, 1920, 0.8);

          // Queue the event with the blob
          await queueEvent(
            EventTypes.PHOTO_ADD,
            {
              category: category,
              description: notes,
            },
            {
              orgId,
              jobId: job.id,
              userId: currentUser?.id,
              gps: gps ? { lat: gps.latitude, lng: gps.longitude } : null,
            },
            compressed,
          );

          queuedCount++;
        } catch (error) {
          console.error(`Failed to queue photo ${i + 1}:`, error);
        }
      }

      // Clean up and reset
      pendingPhotos.forEach((p) => URL.revokeObjectURL(p.previewUrl));
      setPendingPhotos([]);
      setUploading(false);
      setUploadProgress({ current: 0, total: 0 });
      setCategory("progress");
      setNotes("");
      setRawTranscript("");
      setShowTranscript(false);

      if (queuedCount > 0) {
        toast.info(
          `${queuedCount} photo${queuedCount > 1 ? "s" : ""} saved locally. Will upload when back online.`,
        );
      }
      return;
    }

    let successCount = 0;

    for (let i = 0; i < pendingPhotos.length; i++) {
      setUploadProgress({ current: i + 1, total: pendingPhotos.length });

      try {
        const { file } = pendingPhotos[i];

        // Compress image
        const compressed = await compressImage(file, 1920, 0.8);

        const timestamp = Date.now();
        const fileName = `${timestamp}.jpg`;
        let photoPath = null;

        // Upload directly to time4subs R2 via Cloudflare Worker
        try {
          const date = new Date().toISOString().split("T")[0];
          const key = `job-photos/${orgId}/${job.id}/${date}/${timestamp}.jpg`;

          const uploadResponse = await fetch(
            `${PHOTO_UPLOAD_URL}?key=${encodeURIComponent(key)}`,
            {
              method: "PUT",
              body: compressed,
              headers: { "Content-Type": "image/jpeg" },
            },
          );

          if (uploadResponse.ok) {
            const result = await uploadResponse.json();
            photoPath = result.key;
          } else {
            const errText = await uploadResponse.text().catch(() => "");
            console.error("R2 upload failed:", uploadResponse.status, errText);
            toast.error(`Photo ${i + 1} upload failed`);
            continue;
          }
        } catch (uploadErr) {
          console.error("Upload error:", uploadErr.message || uploadErr);
          toast.error(`Photo ${i + 1} upload failed`);
          continue;
        }

        // Save to database
        await api.saveJobPhoto({
          orgId,
          jobId: job.id,
          takenBy: currentUser?.id,
          photoUrl: photoPath,
          category: category,
          description: notes,
          latitude: gps?.latitude || location?.latitude,
          longitude: gps?.longitude || location?.longitude,
        });

        successCount++;
      } catch (error) {
        console.error(`Failed to upload photo ${i + 1}:`, error);
      }
    }

    // Clean up previews and reset form
    pendingPhotos.forEach((p) => URL.revokeObjectURL(p.previewUrl));
    setPendingPhotos([]);
    setUploading(false);
    setUploadProgress({ current: 0, total: 0 });
    setCategory("progress");
    setNotes("");
    setRawTranscript("");
    setShowTranscript(false);

    // Reload photos
    await loadPhotos();

    // Show appropriate feedback
    if (successCount === pendingPhotos.length) {
      toast.success(
        `Uploaded ${successCount} photo${successCount !== 1 ? "s" : ""}`,
      );
    } else if (successCount > 0) {
      toast.warning(
        `Uploaded ${successCount} of ${pendingPhotos.length} photos. Some failed.`,
      );
    }
  };

  // Cancel pending uploads
  const cancelPending = () => {
    pendingPhotos.forEach((p) => URL.revokeObjectURL(p.previewUrl));
    setPendingPhotos([]);
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc" }}>
      {/* Offline Banner */}
      <OfflineBanner
        isOnline={isOnline}
        isSyncing={isSyncing}
        pendingCount={pendingCount}
        onSyncNow={syncAll}
      />

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        capture="environment"
        onChange={handleFileSelect}
        style={{ display: "none" }}
      />

      {/* Header */}
      <div
        style={{
          background: "#1a5a8e",
          color: "white",
          padding: "12px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          position: "sticky",
          top: 0,
          zIndex: 40,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <button
            onClick={onBack}
            style={{
              width: "40px",
              height: "40px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginLeft: "-8px",
              borderRadius: "8px",
              background: "transparent",
              border: "none",
              color: "white",
              cursor: "pointer",
            }}
          >
            <svg
              width="24"
              height="24"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <h1 style={{ fontSize: "18px", fontWeight: "600", margin: 0 }}>
            Photos
          </h1>
        </div>
      </div>

      {/* Job Bar */}
      <div
        style={{
          background: "rgba(26,90,142,0.05)",
          padding: "12px 16px",
          borderBottom: "1px solid rgba(26,90,142,0.1)",
        }}
      >
        <div style={{ fontSize: "14px", color: "#1a5a8e", fontWeight: "500" }}>
          {job.job_number || job.id} - {job.name}
        </div>
      </div>

      {/* Pending Photos Preview */}
      {pendingPhotos.length > 0 && (
        <div
          style={{
            padding: "16px",
            background: "#fefce8",
            borderBottom: "1px solid #fef08a",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "12px",
            }}
          >
            <span style={{ fontWeight: "600", color: "#854d0e" }}>
              {pendingPhotos.length} photo
              {pendingPhotos.length !== 1 ? "s" : ""} ready
            </span>
            {!uploading && (
              <button
                onClick={cancelPending}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#dc2626",
                  fontSize: "14px",
                  cursor: "pointer",
                }}
              >
                Clear
              </button>
            )}
          </div>

          {/* Preview Grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(5, 1fr)",
              gap: "8px",
              marginBottom: "12px",
            }}
          >
            {pendingPhotos.map((photo, index) => (
              <div
                key={index}
                style={{
                  aspectRatio: "1",
                  background: `url(${photo.previewUrl}) center/cover`,
                  borderRadius: "8px",
                  position: "relative",
                  border: "2px solid #fbbf24",
                }}
              >
                {!uploading && (
                  <button
                    onClick={() => removePendingPhoto(index)}
                    style={{
                      position: "absolute",
                      top: "-6px",
                      right: "-6px",
                      width: "20px",
                      height: "20px",
                      borderRadius: "50%",
                      background: "#dc2626",
                      color: "white",
                      border: "none",
                      fontSize: "12px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    ×
                  </button>
                )}
                <div
                  style={{
                    position: "absolute",
                    bottom: "2px",
                    right: "2px",
                    background: "rgba(0,0,0,0.6)",
                    color: "white",
                    fontSize: "10px",
                    padding: "2px 5px",
                    borderRadius: "4px",
                  }}
                >
                  {index + 1}
                </div>
              </div>
            ))}
          </div>

          {/* Category Selector */}
          <div style={{ marginBottom: "12px" }}>
            <label
              style={{
                display: "block",
                fontSize: "13px",
                fontWeight: "500",
                color: "#854d0e",
                marginBottom: "6px",
              }}
            >
              Category
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              disabled={uploading}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: "8px",
                border: "1px solid #fbbf24",
                background: "white",
                fontSize: "15px",
                color: "#1f2937",
              }}
            >
              {CATEGORIES.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>

          {/* Notes Field with Voice Button */}
          <div style={{ marginBottom: "12px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "6px",
              }}
            >
              <label
                style={{
                  fontSize: "13px",
                  fontWeight: "500",
                  color: "#854d0e",
                }}
              >
                Notes (optional)
              </label>
              <VoiceButton
                contextType="photo_description"
                jobId={job?.id}
                onResult={handleVoiceResult}
                onRecordingChange={setIsRecordingVoice}
                disabled={uploading}
                compact={true}
              />
            </div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={uploading || isRecordingVoice}
              placeholder="Add a description or tap the mic to describe what you're photographing..."
              rows={3}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: "8px",
                border: isRecordingVoice
                  ? "2px solid #dc2626"
                  : "1px solid #fbbf24",
                background: isRecordingVoice ? "#fef2f2" : "white",
                fontSize: "15px",
                color: "#1f2937",
                resize: "none",
                fontFamily: "inherit",
                boxSizing: "border-box",
              }}
            />
            {isRecordingVoice && (
              <div
                style={{
                  fontSize: "12px",
                  color: "#dc2626",
                  marginTop: "4px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <span
                  style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    background: "#dc2626",
                    animation: "pulse 1s infinite",
                  }}
                ></span>
                Recording... Describe the photo location and what you see
              </div>
            )}
            {/* Show raw transcript toggle - only visible when we have a transcript */}
            {rawTranscript && (
              <div style={{ marginTop: "8px" }}>
                <button
                  onClick={() => setShowTranscript(!showTranscript)}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "#6b7280",
                    fontSize: "12px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    padding: "4px 0",
                  }}
                >
                  <span>{showTranscript ? "▼" : "▶"}</span>
                  <span>
                    {showTranscript ? "Hide" : "Show"} original transcript
                  </span>
                </button>
                {showTranscript && (
                  <div
                    style={{
                      marginTop: "6px",
                      padding: "10px 12px",
                      background: "#f8fafc",
                      borderRadius: "8px",
                      border: "1px solid #e2e8f0",
                      fontSize: "13px",
                      color: "#64748b",
                      fontStyle: "italic",
                      lineHeight: "1.5",
                    }}
                  >
                    "{rawTranscript}"
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div style={{ display: "flex", gap: "12px" }}>
            {/* Take More Button */}
            {pendingPhotos.length < 10 && !uploading && (
              <button
                onClick={selectPhotos}
                style={{
                  flex: 1,
                  padding: "14px",
                  borderRadius: "12px",
                  border: "2px solid #3b82f6",
                  background: "white",
                  color: "#3b82f6",
                  fontSize: "16px",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
              >
                📷 Take More
              </button>
            )}
            {/* Upload Button */}
            <button
              onClick={uploadAll}
              disabled={uploading}
              style={{
                flex: 2,
                padding: "14px",
                borderRadius: "12px",
                border: "none",
                background: uploading ? "#94a3b8" : "#16a34a",
                color: "white",
                fontSize: "16px",
                fontWeight: "600",
                cursor: uploading ? "wait" : "pointer",
              }}
            >
              {uploading
                ? `📤 Uploading ${uploadProgress.current}/${uploadProgress.total}...`
                : `📤 Upload ${pendingPhotos.length} Photo${pendingPhotos.length !== 1 ? "s" : ""}`}
            </button>
          </div>
        </div>
      )}

      {/* Take/Select Photos Button */}
      <div style={{ padding: "16px" }}>
        <button
          onClick={selectPhotos}
          disabled={uploading}
          style={{
            width: "100%",
            background: uploading
              ? "linear-gradient(135deg, #94a3b8 0%, #64748b 100%)"
              : "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
            color: "white",
            borderRadius: "16px",
            padding: "24px",
            border: "none",
            cursor: uploading ? "wait" : "pointer",
            boxShadow: "0 4px 12px rgba(59,130,246,0.3)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "8px",
            opacity: uploading ? 0.8 : 1,
          }}
        >
          <span style={{ fontSize: "36px" }}>📷</span>
          <span style={{ fontSize: "18px", fontWeight: "600" }}>
            Take Photos
          </span>
          <span style={{ fontSize: "13px", opacity: 0.8 }}>
            Select up to 10 photos at once
          </span>
        </button>
      </div>

      {/* Today's Photos */}
      <div style={{ padding: "0 16px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "12px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span
              style={{ fontSize: "15px", fontWeight: "600", color: "#334155" }}
            >
              Today's Photos
            </span>
            <span
              style={{
                padding: "2px 10px",
                borderRadius: "999px",
                background: "#1a5a8e",
                color: "white",
                fontSize: "12px",
                fontWeight: "600",
              }}
            >
              {photos.length}
            </span>
            {queuedPhotoCount > 0 && (
              <span
                style={{
                  padding: "2px 10px",
                  borderRadius: "999px",
                  background: "#f59e0b",
                  color: "white",
                  fontSize: "12px",
                  fontWeight: "600",
                }}
              >
                +{queuedPhotoCount} pending
              </span>
            )}
          </div>
          {loading && (
            <span style={{ fontSize: "13px", color: "#94a3b8" }}>
              Loading...
            </span>
          )}
        </div>

        {photos.length === 0 && !loading ? (
          <div
            style={{
              background: "#f1f5f9",
              borderRadius: "12px",
              padding: "24px",
              textAlign: "center",
              color: "#64748b",
            }}
          >
            No photos yet today. Tap the button above to take some!
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: "8px",
            }}
          >
            {photos.map((photo) => (
              <div
                key={photo.id}
                style={{
                  aspectRatio: "1",
                  background: photoUrls[photo.photo_url]
                    ? `url(${photoUrls[photo.photo_url]}) center/cover`
                    : "#e2e8f0",
                  borderRadius: "12px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#94a3b8",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                {!photoUrls[photo.photo_url] && (
                  <span style={{ fontSize: "24px" }}>🖼️</span>
                )}
                {/* Category badge */}
                {photo.category && photo.category !== "progress" && (
                  <span
                    style={{
                      position: "absolute",
                      top: "6px",
                      left: "6px",
                      fontSize: "10px",
                      background:
                        photo.category === "issue" ? "#dc2626" : "#3b82f6",
                      color: "white",
                      padding: "2px 6px",
                      borderRadius: "4px",
                      textTransform: "capitalize",
                    }}
                  >
                    {photo.category}
                  </span>
                )}
                <span
                  style={{
                    position: "absolute",
                    bottom: "8px",
                    fontSize: "11px",
                    background: "rgba(0,0,0,0.5)",
                    color: "white",
                    padding: "2px 6px",
                    borderRadius: "4px",
                  }}
                >
                  {formatTime(photo.taken_at)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info Card */}
      <div style={{ padding: "16px", marginTop: "16px" }}>
        <div
          style={{
            background: "#eff6ff",
            border: "1px solid #bfdbfe",
            borderRadius: "12px",
            padding: "16px",
            display: "flex",
            gap: "12px",
          }}
        >
          <div style={{ color: "#3b82f6", fontSize: "18px" }}>💡</div>
          <div style={{ fontSize: "14px", color: "#1e40af" }}>
            <strong>Batch upload.</strong> Select up to 10 photos, review them,
            then upload all at once.
          </div>
        </div>
      </div>
    </div>
  );
}
